import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import Button from '../../../components/misc/Button';
import OpenActionsCard from '../../../components/misc/OpenActionsCard';
import { getRequestsAndInvites } from '../../../services/invitationService';
import { InboxBody } from '../../../types/inbox';

export default function InboxPage(): JSX.Element {
  const { t } = useTranslation();
  const [inbox, setInbox] = useState<InboxBody | null>(null);

  // Filters (empty = no filter / show all)
  const [filters, setFilters] = useState<string[]>([]);

  const FILTER_OPTIONS = [
    { type: 'TASK', label: t('page.inbox.tasks') },
    { type: 'EVENT_INVITE', label: t('page.inbox.eventInvites') },
    { type: 'COMMUNITY_INVITE', label: t('page.inbox.communityInvites') },
    { type: 'CONNECTION_INVITE', label: t('page.inbox.connectionInvites') },
    { type: 'INTRODUCTION', label: t('page.inbox.totalIntroductions') },
    { type: 'DATA_REQUEST', label: t('page.inbox.dataRequests') },
    { type: 'DASHBOARD_CHANGED_DATA_REQUEST', label: t('page.inbox.changedDataRequests') },
    { type: 'PAYMENT_REQUEST', label: t('page.inbox.paymentRequests') },
    { type: 'P2B_PAYMENT', label: t('page.inbox.paymentRequests') },
    { type: 'COMMUNITY_REQUEST', label: t('page.inbox.communityRequests') },
  ];

  const typeCounts = React.useMemo(() => {
    const c: Record<string, number> = {};
    if (!inbox) return c;
    c.TASK = (inbox as any).tasks?.length || 0;
    c.EVENT_INVITE = inbox.eventInvites?.length || 0;
    c.COMMUNITY_INVITE = inbox.communityInvites?.length || 0;
    c.CONNECTION_INVITE = inbox.connectionInvites?.length || 0;
    c.INTRODUCTION = inbox.introductions?.length || 0;
    c.DATA_REQUEST = inbox.dataRequests?.length || 0;
    c.DASHBOARD_CHANGED_DATA_REQUEST = (inbox as any).communityChangedDataRequests?.length || 0;
    c.PAYMENT_REQUEST = (inbox as any).paymentRequests?.length || 0;
    c.P2B_PAYMENT = (inbox as any).paymentRequests?.length || 0;
    c.COMMUNITY_REQUEST = inbox.communityRequests?.length || 0;
    return c;
  }, [inbox]);

  const toggleFilter = (type: string) => {
    setFilters((prev) => (prev.includes(type) ? prev.filter((p) => p !== type) : [...prev, type]));
  };

  const clearFilters = () => setFilters([]);

  useEffect(() => {
    let cancelled = false;
    getRequestsAndInvites()
      .then((data) => {
        if (!cancelled) setInbox(data);
      })
      .catch((err: unknown) => console.error('Failed to load inbox', err));

    return () => {
      cancelled = true;
    };
  }, []);

  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen flex-col">
      <div className="mx-auto w-full px-4 py-6 md:p-8">
        <div className="mb-4 flex flex-col gap-3">
          <Button
            variant="primary"
            onClick={() => navigate(-1)}
            className="items-center gap-2"
            aria-label={t('general.back')}>
            <FaArrowLeft />
            <span>{t('general.back')}</span>
          </Button>

          <h1 className="text-2xl font-semibold sm:text-3xl">{t('page.inbox.title')}</h1>
        </div>

        <div className="flex flex-col gap-6 md:flex-row">
          <div className="order-2 w-full md:order-1 md:w-3/4">
            <OpenActionsCard
              inboxInfo={inbox}
              hideViewLink
              itemsLimit={null}
              filterTypes={filters.length ? filters : undefined}
            />
          </div>

          <aside id="inbox-filters" className="order-1 mb-4 w-full md:order-2 md:mb-0 md:w-1/4">
            <div className="mb-4 rounded-xl border">
              <div className="flex items-center gap-3 rounded-t-xl bg-secondary-200 px-4 py-2">
                <h3 className="text-secondary-900 font-semibold">{t('general.filters')}</h3>
              </div>

              <div className="max-h-[20vh] overflow-y-auto p-4 md:max-h-[70vh]">
                <div className="flex flex-col gap-2">
                  {FILTER_OPTIONS.map((opt) => (
                    <label
                      key={opt.type}
                      className="flex items-center justify-between gap-2 rounded p-3 text-sm hover:bg-secondary-50">
                      <div className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={filters.includes(opt.type)}
                          onChange={() => toggleFilter(opt.type)}
                          className="h-4 w-4 !border-primary"
                        />
                        <span>{opt.label}</span>
                      </div>
                      <span className="bg-secondary-100 text-secondary-900 ml-2 inline-flex min-w-[28px] items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold">
                        {typeCounts[opt.type] || 0}
                      </span>
                    </label>
                  ))}

                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded bg-primary px-2 py-1 text-sm text-secondary">
                      {t('general.clear')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
